// src/views/pages/booking-engine/admin/BeGuestDatabase.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row, Table, Tag, Button, Popover, Input, Dropdown, Menu, message } from "antd";
import { Filter, Search, Download } from "react-feather";
import Select from "react-select";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import debounce from "lodash.debounce";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

import exportService from "../../hms/utils/exportService";
import { useTranslation } from "react-i18next";

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

// ---------- utils ----------
const asInt = (x, def = 0) => {
  const n = parseInt(x, 10);
  return Number.isFinite(n) ? n : def;
};

const BeGuestDatabase = () => {
  const { t } = useTranslation();
  const token = useMemo(() => localStorage.getItem("token") || "", []);

const businessMoment = useMemo(() => moment().startOf("day"), []);

  // ✅ Default: current month to business date
  const [dateRange, setDateRange] = useState({
    startDate: businessMoment.clone().startOf("month"),
    endDate: businessMoment.clone(),
  });

  // Server filters (advanced only — date is NOT here)
  const [filters, setFilters] = useState({
    search: "",
    country: null,
    guestType: null, // New | Returning
    isVerified: null,
    isAuthenticated: null,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);

  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [metrics, setMetrics] = useState({
    totalGuests: 0,
    verifiedGuests: 0,
    returningGuests: 0,
    recentStays: 0,
  });

  // Prevent race conditions
  const activeReqId = useRef(0);

  // Options
  const guestTypeOptions = useMemo(
    () => [
      { value: "New", label: t("new") || "New" },
      { value: "Returning", label: t("returning") || "Returning" },
    ],
    [t]
  );

  const yesNoOptions = useMemo(
    () => [
      { value: true, label: t("yes") || "Yes" },
      { value: false, label: t("no") || "No" },
    ],
    [t]
  );

  // Replace with your existing countries options (from countries-list) if needed
  const countryOptions = useMemo(
    () => [
      { value: "India", label: "India" },
      { value: "United States", label: "United States" },
      { value: "United Kingdom", label: "United Kingdom" },
      { value: "UAE", label: "UAE" },
    ],
    []
  );

  // Export headers (BE)
  const exportHeaders = useMemo(
    () => [
      { key: "guest_id", label: "Guest ID" },
      { key: "full_name", label: t("full_name") || "Full Name" },
      { key: "email", label: t("email") || "Email" },
      { key: "phone_no", label: t("phone") || "Phone" },
      { key: "country", label: t("country") || "Country" },
      { key: "city", label: t("city") || "City" },
      { key: "is_verified", label: "Verified", render: (v) => (v ? "Yes" : "No") },
      { key: "is_authenticated", label: "Authenticated", render: (v) => (v ? "Yes" : "No") },
      { key: "booking_count", label: "Confirmed Bookings" },
      { key: "last_check_in_date", label: "Last Stay" },
      { key: "source", label: "Source" },
      { key: "created_at", label: "Created At" },
      { key: "updated_at", label: "Updated At" },
    ],
    [t]
  );

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      setFilters((prev) => ({ ...prev, search: query }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 450),
    []
  );

  const onSearchChange = (e) => {
    const q = e.target.value || "";
    setSearchQuery(q);
    debouncedSearch(q.trim());
  };

  // Metrics
  const fetchMetrics = useCallback(async () => {
    if (!token) return;
    setMetricsLoading(true);
    try {
      const res = await axios.get(`${apiURL}/booking-engine/guest-metrics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.data?.success) {
        const m = res.data.metrics || {};
        setMetrics({
          totalGuests: asInt(m.totalGuests, 0),
          verifiedGuests: asInt(m.verifiedGuests, 0),
          returningGuests: asInt(m.returningGuests, 0),
          recentStays: asInt(m.recentStays, 0),
        });
      } else {
        setMetrics({ totalGuests: 0, verifiedGuests: 0, returningGuests: 0, recentStays: 0 });
      }
    } catch {
      setMetrics({ totalGuests: 0, verifiedGuests: 0, returningGuests: 0, recentStays: 0 });
    } finally {
      setMetricsLoading(false);
    }
  }, [token]);

  // Guest list
  const fetchGuests = useCallback(async () => {
    if (!token) return;

    const reqId = ++activeReqId.current;
    setLoading(true);

    try {
      const params = {
        search: filters.search || undefined,
        country: filters.country?.value || undefined,
        guestType: filters.guestType?.value || undefined,
        isVerified: filters.isVerified?.value ?? undefined,
        isAuthenticated: filters.isAuthenticated?.value ?? undefined,
        startDate: dateRange.startDate.format("YYYY-MM-DD"),
        endDate: dateRange.endDate.format("YYYY-MM-DD"),
        page: pagination.current,
        pageSize: pagination.pageSize,
      };

      const res = await axios.get(`${apiURL}/booking-engine/guests`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (reqId !== activeReqId.current) return;

      if (res?.data?.success) {
        setData(res.data.guests || []);
        setPagination((prev) => ({
          ...prev,
          total: asInt(res?.data?.pagination?.totalRecords, 0),
        }));
      } else {
        throw new Error(res?.data?.message || "Failed to fetch guests");
      }
    } catch (err) {
      if (reqId !== activeReqId.current) return;

      Swal.fire({
        title: t("error") || "Error",
        text: err?.message || (t("failed_fetch_guests") || "Failed to fetch guests."),
        icon: "error",
      });

      setData([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      if (reqId === activeReqId.current) setLoading(false);
    }
  }, [token, filters, dateRange, pagination.current, pagination.pageSize, t]);

  useEffect(() => {
    fetchMetrics();
    fetchGuests();
  }, [fetchMetrics, fetchGuests]);

  const handleTableChange = (pg) => {
    setPagination((prev) => ({
      ...prev,
      current: pg.current,
      pageSize: pg.pageSize,
    }));
  };

  // Columns (enterprise)
  const columns = useMemo(
    () => [
      {
        title: t("guest_name") || "Guest",
        dataIndex: "full_name",
        key: "full_name",
        fixed: "left",
        width: 220,
        render: (v, r) => (
          <div style={{ lineHeight: 1.25 }}>
            <div style={{ fontWeight: 800, color: "#1677ff" }}>{v || "—"}</div>
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              ID: {r.guest_id} • {r.source ? String(r.source).replaceAll("_", " ") : "—"}
            </div>
          </div>
        ),
      },
      {
        title: t("contact") || "Contact",
        key: "contact",
        width: 240,
        render: (_, r) => (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600 }}>{r.email || <span style={{ color: "#9ca3af" }}>—</span>}</div>
            <div style={{ color: "#6b7280" }}>{r.phone_no || <span style={{ color: "#9ca3af" }}>—</span>}</div>
          </div>
        ),
      },
      {
        title: t("location") || "Location",
        key: "location",
        width: 170,
        render: (_, r) => (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600 }}>{r.country || "—"}</div>
            <div style={{ color: "#6b7280" }}>{r.city || "—"}</div>
          </div>
        ),
      },
      {
        title: "Verified",
        dataIndex: "is_verified",
        key: "is_verified",
        width: 110,
        align: "center",
        render: (v) => (v ? <Tag color="green">Verified</Tag> : <Tag>—</Tag>),
      },
      {
        title: "Auth",
        dataIndex: "is_authenticated",
        key: "is_authenticated",
        width: 90,
        align: "center",
        render: (v) => (v ? <Tag color="blue">Yes</Tag> : <Tag>—</Tag>),
      },
      {
        title: "Bookings",
        dataIndex: "booking_count",
        key: "booking_count",
        width: 110,
        align: "center",
        render: (n) => {
          const c = asInt(n, 0);
          return <Tag color={c >= 2 ? "geekblue" : "default"}>{c}</Tag>;
        },
      },
      {
        title: "Type",
        key: "type",
        width: 120,
        align: "center",
        render: (_, r) => (asInt(r.booking_count, 0) >= 1 ? <Tag color="purple">Returning</Tag> : <Tag>New</Tag>),
      },
      {
        title: "Last Stay",
        dataIndex: "last_check_in_date",
        key: "last_check_in_date",
        width: 140,
        render: (d) => (d ? moment(d).format("DD MMM YYYY") : "—"),
      },
      {
        title: "Created",
        dataIndex: "created_at",
        key: "created_at",
        width: 170,
        render: (v) => (v ? moment(v).format("DD MMM YYYY, hh:mm A") : "—"),
      },
    ],
    [t]
  );

  // Advanced filter popover (NO DATE)
  const filterContent = (
    <div style={{ width: 420, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <div className="ns-filter-label">Country</div>
        <Select
          options={countryOptions}
          value={filters.country}
          onChange={(selected) => {
            setPagination((p) => ({ ...p, current: 1 }));
            setFilters((prev) => ({ ...prev, country: selected }));
          }}
          placeholder="Select country"
          isClearable
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div className="ns-filter-label">Guest Type</div>
        <Select
          options={guestTypeOptions}
          value={filters.guestType}
          onChange={(selected) => {
            setPagination((p) => ({ ...p, current: 1 }));
            setFilters((prev) => ({ ...prev, guestType: selected }));
          }}
          placeholder="New / Returning"
          isClearable
        />
      </div>

      <Row gutter={10}>
        <Col span={12}>
          <div className="ns-filter-label">Verified</div>
          <Select
            options={yesNoOptions}
            value={filters.isVerified}
            onChange={(selected) => {
              setPagination((p) => ({ ...p, current: 1 }));
              setFilters((prev) => ({ ...prev, isVerified: selected }));
            }}
            placeholder="Any"
            isClearable
          />
        </Col>
        <Col span={12}>
          <div className="ns-filter-label">Authenticated</div>
          <Select
            options={yesNoOptions}
            value={filters.isAuthenticated}
            onChange={(selected) => {
              setPagination((p) => ({ ...p, current: 1 }));
              setFilters((prev) => ({ ...prev, isAuthenticated: selected }));
            }}
            placeholder="Any"
            isClearable
          />
        </Col>
      </Row>

      <div style={{ marginTop: 14, display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Button
          onClick={() => {
            setFilters((prev) => ({
              ...prev,
              country: null,
              guestType: null,
              isVerified: null,
              isAuthenticated: null,
            }));
            setPagination((p) => ({ ...p, current: 1 }));
            message.success("Filters cleared");
          }}
        >
          Reset
        </Button>
        <Button type="primary" onClick={() => setFilterVisible(false)}>
          Apply
        </Button>
      </div>
    </div>
  );

  // Download menu
  const suffix = `_${moment().format("YYYYMMDD")}`;
  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={() => exportService.exportToCSV(data, exportHeaders, `be_guests${suffix}.csv`)}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={() => exportService.exportToXLS(data, exportHeaders, `be_guests${suffix}.xlsx`, "Guests")}>
        Download XLS
      </Menu.Item>
      <Menu.Item key="pdf" onClick={() => exportService.exportToPDF(data, exportHeaders, "Booking Engine Guests", `be_guests${suffix}.pdf`)}>
        Download PDF
      </Menu.Item>
    </Menu>
  );

  const metricCards = useMemo(
    () => [
      { label: "Total Guests", value: metrics.totalGuests, tone: "primary" },
      { label: "Verified Guests", value: metrics.verifiedGuests, tone: "success" },
      { label: "Returning Guests", value: metrics.returningGuests, tone: "purple" },
      { label: "Recent Stays (30d)", value: metrics.recentStays, tone: "danger" },
    ],
    [metrics]
  );

  const toneColor = (tone) => {
    if (tone === "success") return "#16a34a";
    if (tone === "danger") return "#dc2626";
    if (tone === "purple") return "#7c3aed";
    return "#1677ff";
  };

  // Datepicker ranges like Booking List
  const drpRanges = useMemo(() => {
    const today = businessMoment.clone();
    return {
      Today: [today.clone(), today.clone()],
      "Last 7 Days": [today.clone().subtract(6, "days"), today.clone()],
      "This Month": [today.clone().startOf("month"), today.clone()],
      "Last Month": [today.clone().subtract(1, "month").startOf("month"), today.clone().subtract(1, "month").endOf("month")],
      "Last 30 Days": [today.clone().subtract(29, "days"), today.clone()],
    };
  }, [businessMoment]);

  const onApplyDate = (event, picker) => {
    setPagination((p) => ({ ...p, current: 1 }));
    setDateRange({
      startDate: picker.startDate.clone().startOf("day"),
      endDate: picker.endDate.clone().endOf("day"),
    });
  };

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <div className="guest-database-container">
          {/* Top bar - SAME pattern as Booking List */}
          <div className="page-header page-top-icons mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="ns-icon-circle">
                  <i className="fas fa-users" />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1 }}>Guests</div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>
                    Booking Engine • Total: {pagination.total}
                  </div>
                </div>
              </div>

              {/* Date range always visible (daterangepicker style) */}
              <div className="ns-top-control">
                <div className="ns-top-label">Date Range</div>
                <DateRangePicker
                  initialSettings={{
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    ranges: drpRanges,
                    autoUpdateInput: true,
                    alwaysShowCalendars: true,
                    locale: { format: "DD MMM YYYY" },
                  }}
                  onApply={onApplyDate}
                >
                  <input
                    className="form-control"
                    style={{
                      width: 260,
                      height: 40,
                      borderRadius: 10,
                      border: "1px solid #d9d9d9",
                      paddingLeft: 12,
                      fontWeight: 600,
                    }}
                    readOnly
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                  />
                </DateRangePicker>
              </div>

              {/* Search */}
              <div className="ns-search-wrap">
                <Input
                  prefix={<Search size={16} />}
                  placeholder="Search by name, email, phone"
                  value={searchQuery}
                  onChange={onSearchChange}
                  allowClear
                  className="ns-search"
                />
              </div>

              {/* Actions */}
              <div className="d-flex align-items-center gap-2">
                <Popover
                  content={filterContent}
                  trigger="click"
                  placement="bottomRight"
                  open={filterVisible}
                  onOpenChange={setFilterVisible}
                >
                  <Button className="ns-btn-ghost">
                    <Filter size={16} style={{ marginRight: 8 }} />
                    Filter
                  </Button>
                </Popover>

                <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                  <Button type="primary">
                    <Download size={16} style={{ marginRight: 8 }} />
                    Download
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <Row gutter={[16, 16]} className="mb-4">
            {metricCards.map((c, i) => (
              <Col key={i} xs={24} sm={12} md={6}>
                <Card className="ns-metric-card" bordered={false}>
                  <div className="ns-metric-top">
                    <div className="ns-metric-dot" style={{ background: toneColor(c.tone) }} />
                    <div className="ns-metric-label">{c.label}</div>
                  </div>
                  <div className="ns-metric-value" style={{ color: toneColor(c.tone) }}>
                    {metricsLoading ? "…" : c.value}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Table */}
          <Card className="ns-table-card" bordered={false}>
            <Table
              dataSource={data}
              columns={columns}
              rowKey="guest_id"
              loading={loading}
              scroll={{ x: 1200 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100", "200"],
              }}
              onChange={handleTableChange}
              className="ns-table"
              bordered
            />
          </Card>
        </div>
      </div>

      {/* Scoped styling (enterprise) */}
      <style jsx>{`
        .guest-database-container {
          padding-bottom: 18px;
        }
        .ns-icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          background: rgba(22, 119, 255, 0.1);
          color: #1677ff;
          box-shadow: 0 10px 30px rgba(22, 119, 255, 0.12);
        }
        .ns-top-control {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .ns-top-label {
          font-size: 11px;
          color: #6b7280;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .ns-search-wrap {
          flex: 1;
          display: flex;
          justify-content: center;
          min-width: 280px;
        }
        .ns-search {
          width: 100%;
          max-width: 560px;
          height: 40px;
          border-radius: 10px;
        }
        .ns-btn-ghost {
          height: 40px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #fff;
        }
        .ns-filter-label {
          font-size: 11px;
          color: #6b7280;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 6px;
        }
        .ns-metric-card {
          border-radius: 14px;
          box-shadow: 0 10px 30px rgba(17, 24, 39, 0.06);
          border: 1px solid rgba(229, 231, 235, 0.9);
        }
        .ns-metric-top {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #6b7280;
          font-weight: 700;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .ns-metric-dot {
          width: 9px;
          height: 9px;
          border-radius: 999px;
        }
        .ns-metric-value {
          margin-top: 10px;
          font-size: 28px;
          font-weight: 900;
          letter-spacing: -0.3px;
        }
        .ns-table-card {
          border-radius: 14px;
          box-shadow: 0 10px 30px rgba(17, 24, 39, 0.06);
          border: 1px solid rgba(229, 231, 235, 0.9);
        }
        :global(.ns-table .ant-table-thead > tr > th) {
          background: #fff !important;
          font-weight: 900;
          color: #111827;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        :global(.ns-table .ant-table-tbody > tr > td) {
          font-size: 14px;
          padding: 14px 16px;
        }
        :global(.ns-table .ant-table-tbody > tr:hover > td) {
          background: #f9fafb !important;
        }
      `}</style>
    </div>
  );
};

export default BeGuestDatabase;